import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useUser } from '../contexts/UserContext';
import { collection, query, orderBy, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';

const NotificationItem = ({ notif, uid }) => {
  const { id, message, type, read } = notif;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!uid || !id) return;
    const notifRef = doc(db, 'usuarios', uid, 'notifications', id);
    try {
      await deleteDoc(notifRef);
    } catch (error) {
      console.error("Error al borrar notificación:", error);
    }
  };

  return (
    <div className={`notification-item ${read ? 'read' : 'unread'}`}>
      <span className="notification-icon">
        {type === 'system' ? '★' : '$'}
      </span>
      <p className="notification-message">{message}</p>
      {!read && <div className="unread-dot"></div>}
      <button className="delete-notification-btn" onClick={handleDelete}>×</button>
    </div>
  );
};

const NotificationPanel = ({ onClose }) => {
  const { userData } = useUser();
  const uid = userData?.uid;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 1. Validación preventiva: Si no hay UID, no iniciamos nada.
    if (!uid) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'usuarios', uid, 'notifications'), 
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        // Manejo explícito de lista vacía
        // Si el snapshot está vacío, definimos el array vacío y cortamos la ejecución.
        // Esto evita que el código intente "mapear" o procesar documentos que no existen.
        if (snapshot.empty) {
          console.log("La colección está vacía, limpiando estado...");
          setNotifications([]);
          setLoading(false);
          return; 
        }

        // Si llega aquí, es porque SÍ hay datos
        const notifsData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        setNotifications(notifsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error crítico en listener:", err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  const handleMarkAllAsRead = async () => {
    try {
      const markAllAsRead = httpsCallable(functions, 'markAllNotificationsAsRead');
      await markAllAsRead();
    } catch (err) {
      console.error("Error al marcar como leído:", err);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="notification-status">Cargando notificaciones...</div>;
    }
    
    if (error) {
      return <div className="notification-status">Ocurrió un error al cargar.</div>;
    }

    // 3. Renderizado seguro del estado "Vacío"
    if (!notifications || notifications.length === 0) {
      return (
        <div className="notification-status">
          <p>📭</p>
          <p>No tienes notificaciones nuevas.</p>
        </div>
      );
    }

    return notifications.map((notif) => (
      <NotificationItem key={notif.id} notif={notif} uid={uid} />
    ));
  };

  return (
    <div className="notification-panel-overlay" onClick={onClose}>
      <div className="notification-panel-container" onClick={(e) => e.stopPropagation()}>
        <div className="notification-header">
          <h3>Notificaciones</h3>
          {/* Solo mostramos el botón si hay notificaciones */}
          {notifications.length > 0 && (
            <button onClick={handleMarkAllAsRead} className="mark-as-read-btn">
              Marcar todo como leído
            </button>
          )}
        </div>
        <div className="notification-list">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;